import useToast from '@hooks/ui/useToast';
import { ItemResponse } from '@models/item';
import { RootState } from '@store/store';
import { MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

function useOptionGuard(item: ItemResponse) {
    const { orderLines } = useSelector((state: RootState) => state.item);
    const toast = useToast();
    const { t } = useTranslation('item');

    const isPrevent = useMemo(
        () => {
            const { defaultOptionGroups, personalOptionGroups } = item;
            const allOptionGroups = [...defaultOptionGroups, ...personalOptionGroups];
            const selectedOptions = orderLines.map((line) => [...line.defaultOptions, ...line.personalOptions])
                .flat();
            for (const optionGroup of allOptionGroups) {
                const {
                    optionGroupId, minSelection, maxSelection, options,
                } = optionGroup;
                const selectedQuantity = selectedOptions
                    .filter((og) => og.optionGroupId === optionGroupId)
                    .map((og) => og.options).flat()
                    .map((option) => option.quantity)
                    .reduce((prev, curr) => prev + curr, 0);
                if (selectedQuantity > maxSelection) {
                    return true;
                }
                if (selectedQuantity < minSelection && minSelection <= options.length) {
                    return true;
                }
            }

            return false;
        },
        [orderLines, item],
    );

    const preventMinOptionCount = (e: MouseEvent) => {
        const { defaultOptionGroups, personalOptionGroups } = item;
        const allOptionGroups = [...defaultOptionGroups, ...personalOptionGroups];

        // 옵션 그룹별로 확인
        for (const optionGroup of allOptionGroups) {
            const {
                optionGroupId, minSelection, maxSelection, name, options,
            } = optionGroup;
            const selectedOptions = orderLines.map((line) => [...line.personalOptions, ...line.defaultOptions])
                .flat()
                .filter((og) => og.optionGroupId === optionGroupId)
                .map((og) => og.options)
                .flat();
            // 선택 수량 체크
            const selectedQuantity = selectedOptions
                .map((option) => option.quantity)
                .reduce((prev, curr) => prev + curr, 0);

            if (selectedQuantity > maxSelection) {
                toast.error(t('optionGuard.maxError', { name, max: maxSelection }));
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            if (selectedQuantity < minSelection && minSelection <= options.length) {
                toast.error(t('optionGuard.minError', { name, min: minSelection }));
                e.preventDefault();
                e.stopPropagation();
                return;
            }
        }
    };

    return {
        isPrevent,
        preventMinOptionCount,
    };
}

export default useOptionGuard;
